import { useEffect } from 'react';
import type { QueryStatus } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { ErrorManagement } from '@printdeal/error-management/react';
import { PriceAttribute, productClient, ServicePrices } from '@printdeal/product-selector';

interface UseProductPrices {
  servicesPrice?: ServicePrices,
  status: QueryStatus,
}

interface GetServicePricesParams {
  serviceKeys: string[],
  productKey: string,
  variantSku: string;
  variableAttributes: PriceAttribute[],
  customerGroupId?: string,
  currencyCode?: string,
  country: string,
  postalCode: string,
}

/**
 * Hook for fetching the services price of a product
 */
export const useServicesPrice = (params: GetServicePricesParams, enabled = false): UseProductPrices => {
  const getServicesPriceQuery = useQuery({
    queryKey: ['servicesPrice', ...Object.entries(params)],
    queryFn: () => productClient.getServicesPrice(params),
    enabled,
    placeholderData: (previousData) => previousData,
  });

  useEffect(() => {
    if (getServicesPriceQuery.error) {
      ErrorManagement.exception({
        exception: getServicesPriceQuery.error,
        tags: {
          eventName: 'errorFetchingServicesPrice',
          component: 'useServicesPrice',
          fileLocation: 'useServicesPrice',
        },
        fingerprint: ['product-selector-error-get-services-price'],
        extra: {
          ...params,
          serviceKeys: JSON.stringify(params.serviceKeys),
          variableAttributes: JSON.stringify(params.variableAttributes),
        },
      });
    }
  }, [getServicesPriceQuery.error, params]);

  return {
    servicesPrice: getServicesPriceQuery.data,
    status: getServicesPriceQuery.status,
  };
};
